import {
  CONTACT_IMPORT_COLUMN_NAMES,
  crmStatusPossibleValues,
  customerTypePossibleValues
} from "logic/CrmSpecifications/contactImportColumnNames";

// Suffixed `Column` so a local holding a CELL VALUE below cannot shadow the column it
// was read from — which it silently did on the first pass here.
const {
  customerBudget: budgetColumn,
  crmStatus: crmStatusColumn,
  customerType: customerTypeColumn,
  roomsMin: roomsMinColumn,
  roomsMax: roomsMaxColumn
} = CONTACT_IMPORT_COLUMN_NAMES;

/**
 * Per-column validation for a real-estate leads import, run against the RAW file row after
 * the headers have been mapped onto contact fields.
 *
 * ── These read the file's HUMAN column names, and that is the whole scope ────────
 *
 * A hand-made or third-party sheet names its columns in Hebrew (`תקציב`, `מחיר`), and its
 * values are what a person typed — which is what needs checking. A file EXPORTED by the app
 * names its columns after the contact's own fields and carries values the app itself wrote
 * and already validated, so most of this simply does not fire on one. Both header spellings
 * are accepted where the check is still meaningful.
 */

/** Every header a budget can arrive under: the app's own field name, then the human ones. */
const BUDGET_HEADERS = [budgetColumn.field, budgetColumn.header, "מחיר", "budget"];

type BudgetResult = {
  /** Did the file carry a budget column at all? Nothing is written when it did not. */
  found: boolean;
  /** The parsed number — `undefined` for an empty or unparseable cell. */
  value?: number;
  error?: Error;
};

const parseAndValidateBudget = (row: any): BudgetResult => {
  const header = BUDGET_HEADERS.find(
    (candidate) => row[candidate] !== undefined && row[candidate] !== ""
  );
  if (!header) return { found: false };

  const raw = row[header];
  const digits = raw?.toString().replace(/\D/g, "") ?? "";

  if (!digits) {
    return { found: true, error: new Error(`תקציב לא תקין: '${raw}'`) };
  }

  const value = Number(digits);
  if (!Number.isFinite(value)) {
    return { found: true, error: new Error(`תקציב לא תקין: '${raw}'`) };
  }

  return { found: true, value };
};

const validateRoomSizes = (row: any): Error => {
  try {
    const roomsMin = row[roomsMinColumn.field] ?? row[roomsMinColumn.header];
    if (roomsMin && isNaN(roomsMin)) {
      throw new Error(`מספר חדרים לא תקין: '${roomsMin}'`);
    }

    const roomsMax = row[roomsMaxColumn.field] ?? row[roomsMaxColumn.header];
    if (roomsMax && isNaN(roomsMax)) {
      throw new Error(`מספר חדרים לא תקין: '${roomsMax}'`);
    }
  } catch (error) {
    return error;
  }
};

// Reads the HUMAN column only. An exported file's `customerTypes` holds a JSON array
// (`["מוכר","קונה"]`), which is not one of these labels and is not meant to be — validating
// it here would reject the app's own export.
const validateCustomerType = (row: any): Error => {
  try {
    const customerType = row[customerTypeColumn.header];
    if (customerType && !customerTypePossibleValues.includes(customerType)) {
      throw new Error(`סוג לקוח לא תקין: '${customerType}'`);
    }
  } catch (error) {
    return error;
  }
};

// CRM v2 status column ("סטטוס" / "סטטוס לקוח") — value must be one of the Hebrew status
// labels. Also HUMAN-only: an export writes the stored id (`in_progress`), which the import
// maps back on its own.
const validateCrmStatus = (row: any): Error => {
  try {
    const crmStatus = row[crmStatusColumn.header] ?? row["סטטוס לקוח"];
    if (crmStatus && !crmStatusPossibleValues.includes(crmStatus)) {
      throw new Error(
        `סטטוס לא תקין: '${crmStatus}'. ערכים נתמכים: ${crmStatusPossibleValues.join(", ")}`
      );
    }
  } catch (error) {
    return error;
  }
};

export const validateRealEstateColumnValues = (parsedRow: any, rawRow: any) => {
  const budget = parseAndValidateBudget(rawRow);

  // ⚠️ ONLY WHEN THE FILE ACTUALLY CARRIED A BUDGET COLUMN.
  //
  // This used to be an unconditional `parsedRow.customerBudget = parsedBudget`, where
  // `parsedBudget` was `""` whenever the lookup found nothing — so a file whose budget
  // column this function did not recognise had its budget silently WIPED after the header
  // mapping had already read it correctly. That is what happened to the app's own export
  // (2026-09-01): its header is `customerBudget`, this list held only `תקציב`/`מחיר`/
  // `budget`, and every re-imported contact arrived with an empty budget while the file
  // plainly showed 2,500,000.
  //
  // And a NUMBER, never a string: `""` in a numeric contact field is the exact shape
  // `contacts-data-hygiene` exists about (a string budget forced a 0-0 floor filter and
  // dropped contacts out of listing alerts). An unparseable cell reports an error and
  // leaves the field alone rather than writing a placeholder.
  if (budget.found) {
    if (budget.value === undefined) delete parsedRow.customerBudget;
    else parsedRow.customerBudget = budget.value;
  }

  const roomSizesError = validateRoomSizes(rawRow);
  const customerTypeError = validateCustomerType(rawRow);
  const crmStatusError = validateCrmStatus(rawRow);

  parsedRow.errors.push(budget.error, roomSizesError, customerTypeError, crmStatusError);
  parsedRow.errors = parsedRow.errors.filter((e) => !!e);
};
